/**
 * Broker Management Admin Page
 * 
 * Comprehensive admin interface for managing broker platforms in the DeeTrade system.
 * 
 * Features:
 * - View all brokers with filtering by country
 * - Add new broker platforms
 * - Edit existing broker configurations
 * - Deactivate/activate broker platforms
 * - Manage broker metadata and login configurations
 * 
 * Architecture:
 * - Each tab is a separate component in the ./tabs directory for better maintainability
 * - Tabs can be developed, tested, and modified independently
 * - Design matches AddEditTradeAccount page for consistency
 * 
 * Access: Admin-only (requires admin/superuser scope)
 * 
 * @module BrokerManagement
 */

import React, { useState, useEffect } from 'react';
import { PageHeader, Tabs } from '../../../components/ds';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  FiList, 
  FiPlus, 
  FiEdit,
  FiAlertCircle
} from 'react-icons/fi';

// Import modular tab components
import {
  BrokerListTab,
  AddBrokerTab,
  EditBrokerTab
} from './tabs';

/**
 * Main Broker Management Admin Component
 * 
 * Renders a tabbed interface for broker platform management with admin access control.
 * Each tab is a separate component that can be independently maintained.
 */
const BrokerManagement = () => {
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('list');
  const [selectedBroker, setSelectedBroker] = useState(null);

  // Check admin access (requires superuser or admin role)
  useEffect(() => {
    if (user && !user.is_superuser && user.role !== 'admin') {
      showSnackbar('Access denied. Admin privileges required.', 'error');
    }
  }, [user, showSnackbar]);

  /**
   * Handle edit broker action
   */
  const handleEditBroker = (broker) => {
    setSelectedBroker(broker);
    setActiveTab('edit');
  };

  /**
   * Handle successful broker save (add or edit)
   */
  const handleBrokerSaved = (message = 'Broker saved successfully') => {
    setSelectedBroker(null);
    setActiveTab('list');
    showSnackbar(message, 'success');
  };

  /**
   * Handle cancel action
   */
  const handleCancel = () => {
    setSelectedBroker(null);
    setActiveTab('list');
  };

  /**
   * Tab configuration
   * Add new tabs here by providing id, name, icon, and component
   */
  const tabs = [
    { 
      id: 'list', 
      name: 'Broker List', 
      icon: FiList, 
      component: BrokerListTab,
      props: { onEditBroker: handleEditBroker }
    },
    { 
      id: 'add', 
      name: 'Add Broker', 
      icon: FiPlus, 
      component: AddBrokerTab,
      props: { onBrokerSaved: handleBrokerSaved, onCancel: handleCancel }
    },
    { 
      id: 'edit', 
      name: 'Edit Broker', 
      icon: FiEdit, 
      component: EditBrokerTab,
      props: { 
        broker: selectedBroker, 
        onBrokerSaved: handleBrokerSaved, 
        onCancel: handleCancel 
      },
      hidden: !selectedBroker // Only show when a broker is selected
    },
  ];

  // Get the active tab configuration
  const activeTabConfig = tabs.find(tab => tab.id === activeTab && !tab.hidden);
  const ActiveTabComponent = activeTabConfig?.component;

  return (
    <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg pt-0 px-4 md:px-6 pb-10">
      {/* Standardized page header (common across all pages) */}
      <PageHeader
        title="Broker Management"
        subtitle="Manage broker platforms, configurations, and integration settings"
      />

      {/* Admin Access Warning */}
      {user && !user.is_superuser && user.role !== 'admin' && (
        <div className="mb-6 p-4 bg-[var(--badge-loss-bg)] border border-loss/30 rounded-lg flex items-center gap-3">
          <FiAlertCircle className="text-[var(--badge-loss-fg)] text-xl flex-shrink-0" />
          <div>
            <p className="text-fg-1 font-semibold">
              Admin Access Required
            </p>
            <p className="text-[var(--badge-loss-fg)] text-sm">
              You do not have permission to access this page. Contact your administrator.
            </p>
          </div>
        </div>
      )}

      {/* Tab Navigation (DS underline tabs) */}
      <Tabs
        className="mb-6"
        value={activeTab}
        onChange={setActiveTab}
        tabs={tabs.filter(tab => !tab.hidden).map((tab) => {
          const Icon = tab.icon;
          return {
            label: tab.name,
            value: tab.id,
            icon: <Icon className="text-lg" />,
          };
        })}
      />

      {/* Tab Content */}
      <div className="mt-6">
        {ActiveTabComponent && (
          <ActiveTabComponent {...(activeTabConfig.props || {})} />
        )}
      </div>
    </div>
  );
};

export default BrokerManagement;
